import React, { useEffect, useState, useCallback } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { Row, Col, Container, Button as BootstrapButton, Card, Alert } from 'react-bootstrap';
import { useParams, useLocation } from 'react-router-dom';
import { Config } from '../config';
import useFirebase from '../hooks/useFirebase';
import { AskForConstants, EstimateStatus, Invoice, InvoiceStatus } from '../types/invoiceType';
import DisplayInvoice from './displayInvoice';
import Review from './review';
import StripePayment from './stripePayment';
import Header from './header';
import { getCollectionName } from '../utils/firebase';
import useCompany from '../hooks/useCompany';

/* Allows a user to make payment on an invoice. On this page we also prompt the user for reviews and for referrals, depending on which value is set in the database for what to ask the customer for. */
const Payment = () => {
    const { invoiceId } = useParams();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    // Get URL query parameters
    const amount = searchParams.get('amount');
    const isDeposit = searchParams.get('isDeposit') === 'true';
    const connectedAccountId = searchParams.get('connectedAccountId');
    const companyId = searchParams.get('companyId');

    // Payment result query params (returned from Stripe redirect)
    const paymentIntent = searchParams.get('payment_intent');
    const redirectStatus = searchParams.get('redirect_status');
    const emailFromRedirect = searchParams.get('email');

    const [invoice, setInvoice] = useState<Invoice | null>(null);
    const [paymentAmount, setPaymentAmount] = useState<number>(0);
    const [tipAmount, setTipAmount] = useState<number>(0);
    const [isDepositPayment] = useState<boolean>(isDeposit);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    const { db } = useFirebase();
    const stripePublishableKey = Config.Env.StripePublishableKey;

    console.log("Connected Account ID:", connectedAccountId);
    console.log("Company ID:", companyId);
    const { company } = useCompany(connectedAccountId, companyId);

    // Handle payment redirect result
    useEffect(() => {
        if (paymentIntent && redirectStatus) {
            if (redirectStatus === 'succeeded') {
                // Payment succeeded, redirect to success page
                const successParams = new URLSearchParams();
                successParams.set('payment_intent', paymentIntent);
                if (connectedAccountId) successParams.set('connectedAccountId', connectedAccountId);
                if (companyId) successParams.set('companyId', companyId);
                if (isDeposit) successParams.set('isDeposit', 'true');
                if (amount) successParams.set('amountPaid', amount);
                const successUrl = emailFromRedirect
                    ? `/success/${invoiceId}/${emailFromRedirect}?${successParams.toString()}`
                    : `/success/${invoiceId}?${successParams.toString()}`;
                window.location.href = successUrl;
            } else if (redirectStatus === 'failed') {
                // Payment failed - error will be shown from invoice.lastPaymentError
                // Clear the URL params so error doesn't persist on refresh
                const newUrl = window.location.pathname +
                    (connectedAccountId ? `?connectedAccountId=${connectedAccountId}` : '') +
                    (companyId ? `${connectedAccountId ? '&' : '?'}companyId=${companyId}` : '') +
                    (isDeposit ? `${connectedAccountId || companyId ? '&' : '?'}isDeposit=true` : '') +
                    (emailFromRedirect ? `${connectedAccountId || companyId || isDeposit ? '&' : '?'}email=${emailFromRedirect}` : '');
                window.history.replaceState({}, '', newUrl);
            }
        }
    }, [paymentIntent, redirectStatus, invoiceId, emailFromRedirect, connectedAccountId, companyId, isDeposit]);

    // Set payment error from invoice's lastPaymentError field
    useEffect(() => {
        if (invoice?.lastPaymentStatus === 'failed' && invoice?.lastPaymentError) {
            setPaymentError(invoice.lastPaymentError);
        }
    }, [invoice?.lastPaymentStatus, invoice?.lastPaymentError]);

    useEffect(() => {
        if (invoice?.status === "paid") {
            window.location.href = "/success/" + invoiceId;
        }

        if (invoice?.isEstimate === true && !isDepositPayment) {
            const queryString = connectedAccountId
                ? `?connectedAccountId=${connectedAccountId}`
                : companyId
                    ? `?companyId=${companyId}`
                    : '';
            window.location.href = `/estimate/${invoiceId}${queryString}`;
        }

        // If amount is provided in URL, use that for payment
        if (amount) {
            setPaymentAmount(parseInt(amount));
        } else if (invoice) {
            setPaymentAmount(Math.round(invoice.amount));
        }

        // Update tip amount when invoice changes
        if (invoice?.tipAmount) {
            setTipAmount(invoice.tipAmount);
        }
    }, [invoice, invoiceId, amount, isDepositPayment, connectedAccountId, companyId]);


    const shouldShowReview = () => {
        if (invoice && invoice.isEstimate === false && invoice.askFor === AskForConstants.review && invoice.status === InvoiceStatus.open) {
            return true;
        }
        return false;
    };

    const acceptEstimate = () => {
        if (!invoice) { return; }

        const docRef = doc(db, getCollectionName("invoices"), invoice.id);
        updateDoc(docRef, {
            status: EstimateStatus.accepted
        }).then(() => {
            setInvoice({ ...invoice, status: EstimateStatus.accepted });
            console.log("Document successfully updated!");
        });
    };

    const handleInvoiceUpdate = useCallback((updatedInvoice: Invoice) => {
        setInvoice(updatedInvoice);
        if (updatedInvoice.tipAmount !== undefined) {
            setTipAmount(updatedInvoice.tipAmount);
        }
    }, []);

    return (
        <div className="max-w-4xl mx-auto px-4">
            <Header connectedAccountId={connectedAccountId} companyId={companyId} />

            {shouldShowReview() && (
                <div className="my-5">
                    <Review />
                </div>
            )}

            <Container className="my-4">
                <Row>
                    <Col>
                        <h1 className="text-2xl font-bold text-gray-800 mb-4">
                            {isDepositPayment
                                ? "Solar Screen Deposit Payment"
                                : `${company?.name ?? ""} has sent you an ${invoice?.isEstimate ? "Estimate" : "Invoice"}`
                            }
                        </h1>
                    </Col>
                </Row>
            </Container>


            {isDepositPayment && invoice && (
                <Card className="shadow mb-4">
                    <Card.Header className="bg-primary bg-opacity-10 py-3">
                        <h4 className="m-0">
                            <i className="fas fa-info-circle me-2"></i>
                            Deposit Information
                        </h4>
                    </Card.Header>
                    <Card.Body className="p-4">
                        <Alert variant="info" className="mb-4">
                            <div className="d-flex">
                                <div className="me-3">
                                    <i className="fas fa-info-circle fa-2x"></i>
                                </div>
                                <div>
                                    <h5 className="mb-2">50% Deposit Payment</h5>
                                    <p className="mb-0">
                                        This payment is for the 50% deposit on your solar screen installation.
                                        The remaining balance will be due upon completion of the installation.
                                    </p>
                                </div>
                            </div>
                        </Alert>

                        {invoice.meshColor && invoice.frameColor && (
                            <div className="bg-light p-3 rounded-lg mb-4">
                                <h5 className="border-bottom pb-2 mb-3">Your Color Selections</h5>
                                <Row>
                                    <Col xs={12} md={6} className="mb-2">
                                        <div className="d-flex align-items-center">
                                            <div className={`p-2 rounded-circle me-2 border color-${invoice.meshColor.toLowerCase()}`}
                                                style={{ width: "24px", height: "24px" }}>
                                            </div>
                                            <span><strong>Mesh Color:</strong> {invoice.meshColor}</span>
                                        </div>
                                    </Col>
                                    <Col xs={12} md={6} className="mb-2">
                                        <div className="d-flex align-items-center">
                                            <div className={`p-2 rounded-circle me-2 border color-${invoice.frameColor.toLowerCase()}`}
                                                style={{ width: "24px", height: "24px" }}>
                                            </div>
                                            <span><strong>Frame Color:</strong> {invoice.frameColor}</span>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* {invoice && invoice.isEstimate === false && invoice.askFor === AskForConstants.referral && !isDepositPayment && (
                <div className="bg-light p-4 rounded-lg shadow-sm mb-4">
                    <Referrals referree={invoice.fullName} discountPercent={10} updatePrice={updatePrice} />
                </div>
            )} */}

            {invoiceId && (
                <div className="my-4">
                    <DisplayInvoice invoiceId={invoiceId} setInvoice={handleInvoiceUpdate} />
                </div>
            )}

            {invoice && (invoice.isEstimate === false || isDepositPayment) && (
                <div className="my-4">
                    <StripePayment
                        hostUrl={Config.Env.StripeUrl}
                        publishableKey={stripePublishableKey}
                        price={paymentAmount + tipAmount}
                        tipAmount={tipAmount}
                        isEstimate={invoice.isEstimate}
                        isDeposit={isDepositPayment}
                        documentId={invoice.id}
                        connectedAccountId={connectedAccountId}
                        paymentError={paymentError}
                        onErrorDismiss={() => setPaymentError(null)}
                        defaultEmail={emailFromRedirect || undefined}
                    />
                </div>
            )}

            {invoice?.isEstimate === true && invoice?.status === EstimateStatus.open && (
                <BootstrapButton
                    className="modern-cta-button w-100 py-3 mt-3 mb-5"
                    onClick={acceptEstimate}
                >
                    Accept Estimate
                </BootstrapButton>
            )}
        </div>
    );
};

export default Payment;
